import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import API_CONFIG from '../utils/apiConfig';
import { formatPaymentNetPayable } from '../utils/paymentDisplay';
import SettingsDropdown from './SettingsDropdown';
import CronLogsTab from './CronLogsTab';
import ActionLogsTab from './ActionLogsTab';
import './SuperAdminDashboard.css';
import '../styles/pagination-mobile.css';

const DetailRow = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{label}</span>
    <span style={{ color: '#fff' }}>{value || 'N/A'}</span>
  </div>
);

/** Primary organisation label for employers (org profile name, aligned with subcontractor-facing copy). */
function employerOrganizationDisplayName(row) {
  if (!row) return 'N/A';
  const o = (v) => (v != null && String(v).trim() !== '' ? String(v).trim() : '');
  return (
    o(row.organization_name) ||
    o(row.registered_name) ||
    o(row.full_name) ||
    row.email ||
    'N/A'
  );
}

const SuperAdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState('overview');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [subcontractors, setSubcontractors] = useState([]);
  const [subcontractorsLoading, setSubcontractorsLoading] = useState(false);
  
  // Pagination state
  const [companiesPage, setCompaniesPage] = useState(1);
  const [companiesPerPage, setCompaniesPerPage] = useState(10);
  const [companiesTotal, setCompaniesTotal] = useState(0);
  const [companiesTotalPages, setCompaniesTotalPages] = useState(0);
  const [companiesStatusFilter, setCompaniesStatusFilter] = useState('');
  
  const [subcontractorsPage, setSubcontractorsPage] = useState(1);
  const [subcontractorsPerPage, setSubcontractorsPerPage] = useState(10);
  const [subcontractorsTotal, setSubcontractorsTotal] = useState(0);
  const [subcontractorsTotalPages, setSubcontractorsTotalPages] = useState(0);
  
  // Payments state
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsPerPage, setPaymentsPerPage] = useState(10);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsTotalPages, setPaymentsTotalPages] = useState(0);
  const [paymentsMonth, setPaymentsMonth] = useState(null);
  const [paymentsYear, setPaymentsYear] = useState(new Date().getFullYear());
  const [paymentsEmployerFilter, setPaymentsEmployerFilter] = useState('');
  const [paymentsStatusFilter, setPaymentsStatusFilter] = useState('');
  const [allEmployers, setAllEmployers] = useState([]);
  const [paymentsSummary, setPaymentsSummary] = useState(null);
  
  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [userDetailType, setUserDetailType] = useState(null); // 'employer' | 'subcontractor'
  
  // SMTP Settings state
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: '',
    smtp_user: '',
    smtp_password: '',
    smtp_tls: true,
    emails_from_email: '',
    emails_from_name: '',
    brevo_api_key: ''
  });
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpMessage, setSmtpMessage] = useState({ type: '', text: '' });
  const [testEmail, setTestEmail] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);

  // Monthly Returns state
  const [availableCISMonths, setAvailableCISMonths] = useState([]);
  const [monthlyReturnsYear, setMonthlyReturnsYear] = useState(null);
  const [monthlyReturnsMonth, setMonthlyReturnsMonth] = useState(null);
  const [monthlyReturnsEmployerFilter, setMonthlyReturnsEmployerFilter] = useState('');
  const [monthlyReturnsList, setMonthlyReturnsList] = useState([]);
  const [monthlyReturnsLoading, setMonthlyReturnsLoading] = useState(false);
  const [monthlyReturnsDetail, setMonthlyReturnsDetail] = useState(null);
  const [monthlyReturnsDetailLoading, setMonthlyReturnsDetailLoading] = useState(false);
  const [showMonthlyReturnsDetailModal, setShowMonthlyReturnsDetailModal] = useState(false);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    fetchStats();
    if (activeMenu === 'companies') {
      fetchCompanies();
    } else if (activeMenu === 'subcontractors') {
      fetchSubcontractors();
    } else if (activeMenu === 'payments') {
      fetchPayments();
      fetchAllEmployers();
    } else if (activeMenu === 'smtp') {
      fetchSMTPSettings();
    } else if (activeMenu === 'monthly-returns') {
      fetchAvailableCISMonths();
      fetchAllEmployers();
      if (monthlyReturnsYear && monthlyReturnsMonth) {
        fetchMonthlyReturns();
      } else {
        setMonthlyReturnsList([]);
      }
    }
  }, [activeMenu, companiesPage, companiesPerPage, companiesStatusFilter, subcontractorsPage, subcontractorsPerPage, paymentsPage, paymentsPerPage, paymentsMonth, paymentsYear, paymentsEmployerFilter, paymentsStatusFilter, monthlyReturnsYear, monthlyReturnsMonth, monthlyReturnsEmployerFilter]);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const url = API_CONFIG.getApiUrl(API_CONFIG.endpoints.getSuperAdminStats);
      console.log('Fetching stats from:', url);
      
      const response = await API_CONFIG.fetchWithAuth(url, { method: 'GET' });
      console.log('Stats response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Super Admin Stats Response:', data); // Debug log
        console.log('Companies stats:', data.companies); // Debug log
        console.log('Payments stats:', data.payments); // Debug log
        
        // Ensure all required fields exist
        const statsData = {
          companies: data.companies || { total: 0, pending: 0, approved: 0, rejected: 0, active: 0, inactive: 0 },
          subcontractors: data.subcontractors || { total: 0 },
          users: data.users || { active: 0 },
          hmrc: data.hmrc || { connected_companies: 0 },
          payments: data.payments || { total: 0, pending: 0, monthly_data: [] }
        };
        
        setStats(statsData);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Error fetching stats:', response.status, errorData);
        // Set empty stats object to prevent "No statistics available" message
        setStats({
          companies: { total: 0, pending: 0, approved: 0, rejected: 0, active: 0, inactive: 0 },
          subcontractors: { total: 0 },
          users: { active: 0 },
          hmrc: { connected_companies: 0 },
          payments: { total: 0, pending: 0, monthly_data: [] }
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      console.error('Error details:', error.message, error.stack);
      // Set empty stats object to prevent "No statistics available" message
      setStats({
        companies: { total: 0, pending: 0, approved: 0, rejected: 0, active: 0, inactive: 0 },
        subcontractors: { total: 0 },
        users: { active: 0 },
        hmrc: { connected_companies: 0 },
        payments: { total: 0, pending: 0, monthly_data: [] }
      });
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getSuperAdminCompanies));
      url.searchParams.append('page', companiesPage);
      url.searchParams.append('per_page', companiesPerPage);
      if (companiesStatusFilter && companiesStatusFilter !== 'all' && companiesStatusFilter !== '') {
        url.searchParams.append('status_filter', companiesStatusFilter);
      }

      const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
      if (response.ok) {
        const data = await response.json();
        console.log('Companies data:', data); // Debug log
        setCompanies(data.items || []);
        setCompaniesTotal(data.total || 0);
        setCompaniesTotalPages(data.total_pages || 0);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Error fetching companies:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const fetchSubcontractors = async () => {
    setSubcontractorsLoading(true);
    try {
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getSuperAdminSubcontractors));
      url.searchParams.append('page', subcontractorsPage);
      url.searchParams.append('per_page', subcontractorsPerPage);

      const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
      if (response.ok) {
        const data = await response.json();
        console.log('Subcontractors data:', data); // Debug log
        setSubcontractors(data.items || []);
        setSubcontractorsTotal(data.total || 0);
        setSubcontractorsTotalPages(data.total_pages || 0);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Error fetching subcontractors:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error fetching subcontractors:', error);
    } finally {
      setSubcontractorsLoading(false);
    }
  };

  const handleApproveCompany = async (company) => {
    setActionLoading(true);
    setActionMessage({ type: '', text: '' });
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.approveCompanyRegistration}/${company.user_id}/approve`),
        { method: 'POST' }
      );
      if (response.ok) {
        setActionMessage({ type: 'success', text: 'Employer registration approved successfully' });
        setTimeout(() => setActionMessage({ type: '', text: '' }), 3000);
        fetchCompanies();
        fetchStats();
      } else {
        const errorData = await response.json();
        setActionMessage({ type: 'error', text: errorData.detail || 'Failed to approve employer' });
        setTimeout(() => setActionMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      setActionMessage({ type: 'error', text: 'Network error. Please try again.' });
      setTimeout(() => setActionMessage({ type: '', text: '' }), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectCompany = async () => {
    if (!selectedCompany) return;
    
    setActionLoading(true);
    setActionMessage({ type: '', text: '' });
    try {
      const url = new URL(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.rejectCompanyRegistration}/${selectedCompany.user_id}/reject`)
      );
      if (rejectionReason) {
        url.searchParams.append('rejection_reason', rejectionReason);
      }

      const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'POST' });
      if (response.ok) {
        setActionMessage({ type: 'success', text: 'Employer registration rejected' });
        setTimeout(() => setActionMessage({ type: '', text: '' }), 3000);
        setShowRejectModal(false);
        setRejectionReason('');
        setSelectedCompany(null);
        fetchCompanies();
        fetchStats();
      } else {
        const errorData = await response.json();
        setActionMessage({ type: 'error', text: errorData.detail || 'Failed to reject employer' });
        setTimeout(() => setActionMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      setActionMessage({ type: 'error', text: 'Network error. Please try again.' });
      setTimeout(() => setActionMessage({ type: '', text: '' }), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (company) => {
    setSelectedCompany(company);
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectionReason('');
    setSelectedCompany(null);
  };

  const fetchAllEmployers = async () => {
    try {
      const baseUrl = API_CONFIG.getApiUrl(API_CONFIG.endpoints.getSuperAdminCompanies);
      const perPage = 100; // API max; must be same on every page or offsets skip rows
      let allEmployersList = [];
      let page = 1;
      let totalPages = 1;

      while (page <= totalPages) {
        const response = await API_CONFIG.fetchWithAuth(
          `${baseUrl}?page=${page}&per_page=${perPage}`,
          { method: 'GET' }
        );
        if (!response.ok) break;
        const data = await response.json();
        allEmployersList = allEmployersList.concat(data.items || []);
        const tp = data.total_pages;
        totalPages = typeof tp === 'number' && tp >= 0 ? tp : 1;
        page += 1;
      }

      setAllEmployers(allEmployersList);
    } catch (error) {
      console.error('Error fetching employers:', error);
    }
  };

  const fetchPayments = async () => {
    setPaymentsLoading(true);
    try {
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getSuperAdminPayments));
      url.searchParams.append('page', paymentsPage);
      url.searchParams.append('per_page', paymentsPerPage);
      
      if (paymentsMonth) {
        url.searchParams.append('month', paymentsMonth);
      }
      if (paymentsYear) {
        url.searchParams.append('year', paymentsYear);
      }
      if (paymentsEmployerFilter) {
        url.searchParams.append('employer_id', paymentsEmployerFilter);
      }
      if (paymentsStatusFilter) {
        url.searchParams.append('status_filter', paymentsStatusFilter);
      }

      const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
      if (response.ok) {
        const data = await response.json();
        setPayments(data.items || []);
        setPaymentsTotal(data.total || 0);
        setPaymentsTotalPages(data.total_pages || 0);
        setPaymentsSummary(data.summary || null);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Error fetching payments:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleEnableCompany = async (company) => {
    setActionLoading(true);
    setActionMessage({ type: '', text: '' });
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.approveCompanyRegistration}/${company.user_id}/enable`),
        { method: 'POST' }
      );
      if (response.ok) {
        setActionMessage({ type: 'success', text: 'Employer enabled successfully' });
        setTimeout(() => setActionMessage({ type: '', text: '' }), 3000);
        fetchCompanies();
        fetchStats();
      } else {
        const errorData = await response.json();
        setActionMessage({ type: 'error', text: errorData.detail || 'Failed to enable employer' });
        setTimeout(() => setActionMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      setActionMessage({ type: 'error', text: 'Network error. Please try again.' });
      setTimeout(() => setActionMessage({ type: '', text: '' }), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisableCompany = async (company) => {
    setActionLoading(true);
    setActionMessage({ type: '', text: '' });
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.approveCompanyRegistration}/${company.user_id}/disable`),
        { method: 'POST' }
      );
      if (response.ok) {
        setActionMessage({ type: 'success', text: 'Employer disabled successfully' });
        setTimeout(() => setActionMessage({ type: '', text: '' }), 3000);
        fetchCompanies();
        fetchStats();
      } else {
        const errorData = await response.json();
        setActionMessage({ type: 'error', text: errorData.detail || 'Failed to disable employer' });
        setTimeout(() => setActionMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      setActionMessage({ type: 'error', text: 'Network error. Please try again.' });
      setTimeout(() => setActionMessage({ type: '', text: '' }), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  const fetchSMTPSettings = async () => {
    setSmtpLoading(true);
    setSmtpMessage({ type: '', text: '' });
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.getSMTPSettings),
        { method: 'GET' }
      );
      if (response.ok) {
        const data = await response.json();
        setSmtpSettings({
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port ?? '',
          smtp_user: data.smtp_user || '',
          smtp_password: data.smtp_password || '',
          smtp_tls: data.smtp_tls !== undefined ? data.smtp_tls : true,
          emails_from_email: data.emails_from_email || '',
          emails_from_name: data.emails_from_name || '',
          brevo_api_key: data.brevo_api_key || ''
        });
      } else {
        const errorData = await response.json();
        setSmtpMessage({ type: 'error', text: errorData.detail || 'Failed to load SMTP settings' });
      }
    } catch (error) {
      setSmtpMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSmtpLoading(false);
    }
  };

  const handleUpdateSMTPSettings = async (e) => {
    e.preventDefault();
    setSmtpSaving(true);
    setSmtpMessage({ type: '', text: '' });
    try {
      const payload = { ...smtpSettings };
      if (payload.smtp_password === '') delete payload.smtp_password;
      if (payload.brevo_api_key === '') delete payload.brevo_api_key;
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.updateSMTPSettings),
        {
          method: 'POST',
          body: JSON.stringify(payload)
        }
      );
      if (response.ok) {
        setSmtpMessage({ type: 'success', text: 'SMTP settings updated successfully' });
        setTimeout(() => setSmtpMessage({ type: '', text: '' }), 3000);
        // Keep current form values after save
      } else {
        const errorData = await response.json();
        setSmtpMessage({ type: 'error', text: errorData.detail || 'Failed to update SMTP settings' });
      }
    } catch (error) {
      setSmtpMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleTestEmail = async (e) => {
    e.preventDefault();
    if (!testEmail || !testEmail.includes('@')) {
      setSmtpMessage({ type: 'error', text: 'Please enter a valid email address' });
      setTimeout(() => setSmtpMessage({ type: '', text: '' }), 3000);
      return;
    }
    
    setTestEmailSending(true);
    setSmtpMessage({ type: '', text: '' });
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.testSMTP}?test_email=${encodeURIComponent(testEmail)}`),
        { method: 'POST' }
      );
      if (response.ok) {
        const data = await response.json();
        setSmtpMessage({ type: 'success', text: data.message || `Test email sent successfully to ${testEmail}` });
        setTimeout(() => setSmtpMessage({ type: '', text: '' }), 5000);
        setTestEmail('');
      } else {
        const errorData = await response.json();
        setSmtpMessage({ type: 'error', text: errorData.detail || 'Failed to send test email' });
      }
    } catch (error) {
      setSmtpMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setTestEmailSending(false);
    }
  };

  const fetchAvailableCISMonths = async () => {
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.getAvailableCISMonths}?limit=24`),
        { method: 'GET' }
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableCISMonths(data);
      }
    } catch (error) {
      console.error('Error fetching CIS months:', error);
    }
  };

  useEffect(() => {
    if (activeMenu === 'monthly-returns' && availableCISMonths.length > 0 && monthlyReturnsYear == null && monthlyReturnsMonth == null) {
      setMonthlyReturnsYear(availableCISMonths[0].year);
      setMonthlyReturnsMonth(availableCISMonths[0].month);
    }
  }, [activeMenu, availableCISMonths, monthlyReturnsYear, monthlyReturnsMonth]);

  const fetchMonthlyReturns = async () => {
    if (!monthlyReturnsYear || !monthlyReturnsMonth) return;
    setMonthlyReturnsLoading(true);
    try {
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getSuperAdminMonthlyReturns));
      url.searchParams.append('year', monthlyReturnsYear);
      url.searchParams.append('month', monthlyReturnsMonth);
      if (monthlyReturnsEmployerFilter) {
        url.searchParams.append('employer_id', monthlyReturnsEmployerFilter);
      }
      const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
      if (response.ok) {
        const data = await response.json();
        setMonthlyReturnsList(data.items || []);
      } else {
        setMonthlyReturnsList([]);
      }
    } catch (error) {
      console.error('Error fetching monthly returns:', error);
      setMonthlyReturnsList([]);
    } finally {
      setMonthlyReturnsLoading(false);
    }
  };

  const openMonthlyReturnsDetail = async (employerId) => {
    if (!monthlyReturnsYear || !monthlyReturnsMonth) return;
    setShowMonthlyReturnsDetailModal(true);
    setMonthlyReturnsDetailLoading(true);
    setMonthlyReturnsDetail(null);
    try {
      const url = `${API_CONFIG.getApiUrl(API_CONFIG.endpoints.getSuperAdminEmployerMonthlySummary)}/${employerId}?year=${monthlyReturnsYear}&month=${monthlyReturnsMonth}`;
      const response = await API_CONFIG.fetchWithAuth(url, { method: 'GET' });
      if (response.ok) {
        const data = await response.json();
        setMonthlyReturnsDetail(data);
      }
    } catch (error) {
      console.error('Error fetching employer monthly summary:', error);
    } finally {
      setMonthlyReturnsDetailLoading(false);
    }
  };

  return (
    <div className="super-admin-dashboard">
      <div className="dashboard-header">
        <div className="header-left">
          <h1>Super Admin Dashboard</h1>
        </div>
        <div className="header-right">
          <SettingsDropdown user={user} onLogout={onLogout} />
        </div>
      </div>

      <div className="dashboard-nav">
        <button
          className={activeMenu === 'overview' ? 'active' : ''}
          onClick={() => setActiveMenu('overview')}
        >
          Overview
        </button>
        <button
          className={activeMenu === 'companies' ? 'active' : ''}
          onClick={() => setActiveMenu('companies')}
        >
          Employers
          {stats && stats.companies.pending > 0 && (
            <span className="badge">{stats.companies.pending}</span>
          )}
        </button>
        <button
          className={activeMenu === 'subcontractors' ? 'active' : ''}
          onClick={() => setActiveMenu('subcontractors')}
        >
          Subcontractors
        </button>
        <button
          className={activeMenu === 'payments' ? 'active' : ''}
          onClick={() => setActiveMenu('payments')}
        >
          Payments
        </button>
        <button
          className={activeMenu === 'monthly-returns' ? 'active' : ''}
          onClick={() => setActiveMenu('monthly-returns')}
        >
          Monthly Returns
        </button>
        <button
          className={activeMenu === 'cron-logs' ? 'active' : ''}
          onClick={() => setActiveMenu('cron-logs')}
        >
          Cron Jobs
        </button>
        <button
          className={activeMenu === 'action-logs' ? 'active' : ''}
          onClick={() => setActiveMenu('action-logs')}
        >
          Action Logs
        </button>
        <button
          className={activeMenu === 'smtp' ? 'active' : ''}
          onClick={() => setActiveMenu('smtp')}
        >
          SMTP
        </button>
      </div>

      {actionMessage.text && (
        <div className={`message ${actionMessage.type}`} style={{ margin: '20px', padding: '15px' }}>
          {actionMessage.text}
        </div>
      )}

      {/* Overview Section */}
      {activeMenu === 'overview' && (
        <div className="overview-section">
          {statsLoading ? (
            <div className="loading-state">Loading statistics...</div>
          ) : stats && (stats.companies || stats.subcontractors || stats.payments) ? (
            <>
              {/* Top Cards */}
              <div className="stats-grid" style={{ marginBottom: '30px' }}>
                <div className="stat-card" style={{ background: 'rgba(102, 126, 234, 0.3)' }}>
                  <h3>Total Employers</h3>
                  <div className="stat-value">{stats.companies.total}</div>
                </div>
                <div className="stat-card" style={{ background: 'rgba(118, 75, 162, 0.3)' }}>
                  <h3>Total Subcontractors</h3>
                  <div className="stat-value">{stats.subcontractors.total}</div>
                </div>
                <div className="stat-card" style={{ background: 'rgba(40, 167, 69, 0.3)' }}>
                  <h3>Total Payments</h3>
                  <div className="stat-value">{stats.payments.total}</div>
                </div>
              </div>

              {/* Charts Grid */}
              <div className="charts-grid">
                {/* Donut Chart - Active/Inactive Employers */}
                <div className="chart-card" style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  minHeight: '400px'
                }}>
                  <h3 style={{ margin: '0 0 20px 0', color: '#fff', fontSize: '18px' }}>Employers Status</h3>
                  {(() => {
                    const pieData = [
                      { name: 'Active', value: (stats.companies && stats.companies.active) ? stats.companies.active : 0 },
                      { name: 'Inactive', value: (stats.companies && stats.companies.inactive) ? stats.companies.inactive : 0 }
                    ];
                    const isMobile = window.innerWidth <= 768;
                    // Use responsive width based on screen size - ensure it fits within card
                    const pieWidth = isMobile ? Math.min(300, window.innerWidth - 80) : 400;
                    const pieHeight = isMobile ? 250 : 300;
                    const pieRadius = isMobile ? 60 : 80;
                    return (
                      <div style={{ width: '100%', height: `${pieHeight}px`, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', maxWidth: '100%' }}>
                        <PieChart width={pieWidth} height={pieHeight}>
                          <Pie
                            data={pieData}
                            cx={pieWidth / 2}
                            cy={pieHeight / 2}
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={pieRadius}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            <Cell fill="#28a745" />
                            <Cell fill="#dc3545" />
                          </Pie>
                          <Tooltip />
                          <Legend 
                            wrapperStyle={{ fontSize: isMobile ? '10px' : '12px', paddingTop: isMobile ? '10px' : '0' }}
                            iconSize={isMobile ? 10 : 12}
                            verticalAlign={isMobile ? 'bottom' : 'top'}
                            align={isMobile ? 'center' : 'center'}
                          />
                        </PieChart>
                      </div>
                    );
                  })()}
                </div>

                {/* Bar Chart - Last 12 Months Payments */}
                <div className="chart-card" style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  minHeight: '400px'
                }}>
                  <h3 style={{ margin: '0 0 20px 0', color: '#fff', fontSize: '18px' }}>Payments - Last 12 Months</h3>
                  {(() => {
                    const barData = stats.payments && stats.payments.monthly_data ? stats.payments.monthly_data : [];
                    const isMobile = window.innerWidth <= 768;
                    // Use responsive width - minimum 600px for readability, but allow scrolling on mobile
                    const barWidth = isMobile ? 600 : 800;
                    const barHeight = isMobile ? 250 : 300;
                    return (
                      <div style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}>
                        {barData.length > 0 ? (
                          <div style={{ minWidth: isMobile ? `${barWidth}px` : '100%', width: isMobile ? `${barWidth}px` : '100%' }}>
                            <BarChart 
                              width={barWidth} 
                              height={barHeight} 
                              data={barData} 
                              margin={{ left: isMobile ? 5 : 10, right: isMobile ? 5 : 10, top: 10, bottom: isMobile ? 80 : 60 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.2)" />
                              <XAxis 
                                dataKey="month" 
                                stroke="#fff"
                                angle={-45}
                                textAnchor="end"
                                height={isMobile ? 100 : 80}
                                tick={{ fill: '#fff', fontSize: isMobile ? 10 : 12 }}
                                interval={0}
                              />
                              <YAxis 
                                stroke="#fff"
                                tick={{ fill: '#fff', fontSize: isMobile ? 10 : 12 }}
                                width={isMobile ? 40 : 60}
                              />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  borderRadius: '8px',
                                  color: '#fff',
                                  fontSize: isMobile ? '11px' : '12px'
                                }}
                              />
                              <Legend 
                                wrapperStyle={{ fontSize: isMobile ? '10px' : '12px', paddingTop: isMobile ? '10px' : '0' }}
                                iconSize={isMobile ? 10 : 12}
                                verticalAlign={isMobile ? 'bottom' : 'top'}
                                align={isMobile ? 'center' : 'center'}
                              />
                              <Bar dataKey="count" fill="#667eea" name={isMobile ? "Payments" : "Number of Payments"} />
                              <Bar dataKey="total" fill="#764ba2" name={isMobile ? "Amount (£)" : "Total Amount (£)"} />
                            </BarChart>
                          </div>
                        ) : (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#fff' }}>
                            No payment data available
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Additional Stats Grid */}
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>Pending Approvals</h3>
                  <div className="stat-value">{stats.companies.pending}</div>
                </div>
                <div className="stat-card">
                  <h3>Approved Companies</h3>
                  <div className="stat-value">{stats.companies.approved}</div>
                </div>
                <div className="stat-card">
                  <h3>Active Users</h3>
                  <div className="stat-value">{stats.users.active}</div>
                </div>
                <div className="stat-card">
                  <h3>Pending Payments</h3>
                  <div className="stat-value">{stats.payments.pending}</div>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">No statistics available</div>
          )}
        </div>
      )}

      {/* Companies Section */}
      {activeMenu === 'companies' && (
        <div className="companies-section">
          <div className="section-header">
            <h2>Employers</h2>
            <div className="filter-tabs">
              <button
                className={!companiesStatusFilter || companiesStatusFilter === 'all' ? 'active' : ''}
                onClick={() => {
                  setCompaniesStatusFilter('all');
                  setCompaniesPage(1);
                }}
              >
                All ({stats?.companies?.total || 0})
              </button>
              <button
                className={companiesStatusFilter === 'active' ? 'active' : ''}
                onClick={() => {
                  setCompaniesStatusFilter('active');
                  setCompaniesPage(1);
                }}
              >
                Enabled ({stats?.companies?.active || 0})
              </button>
              <button
                className={companiesStatusFilter === 'inactive' ? 'active' : ''}
                onClick={() => {
                  setCompaniesStatusFilter('inactive');
                  setCompaniesPage(1);
                }}
              >
                Disabled ({stats?.companies?.inactive || 0})
              </button>
            </div>
          </div>

          {companiesLoading ? (
            <div className="loading-state">Loading employers...</div>
          ) : companies.length > 0 ? (
            <>
              <div className="table-container">
                <table className="companies-table">
                  <thead>
                    <tr>
                      <th>Employer Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>VAT Number</th>
                      <th>UTR</th>
                      <th>Registration Date</th>
                      <th>Account Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((company) => (
                      <tr
                        key={company.user_id}
                        onClick={() => {
                          setSelectedUserDetail(company);
                          setUserDetailType('employer');
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{employerOrganizationDisplayName(company)}</td>
                        <td>{company.email}</td>
                        <td>{company.phone_number || 'N/A'}</td>
                        <td>{company.vat_number || 'N/A'}</td>
                        <td>{company.unique_tax_reference || 'N/A'}</td>
                        <td>{company.created_at ? new Date(company.created_at).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          <span className={`status-badge ${company.status === 'active' ? 'status-approved' : 'status-rejected'}`}>
                            {company.status === 'active' ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {company.status === 'active' ? (
                              <button
                                onClick={() => handleDisableCompany(company)}
                                disabled={actionLoading}
                                className="btn-disable"
                                title="Disable account - employer will not be able to login"
                              >
                                Disable
                              </button>
                            ) : (
                              <button
                                onClick={() => handleEnableCompany(company)}
                                disabled={actionLoading}
                                className="btn-approve"
                                title="Enable account - employer will be able to login"
                              >
                                Enable
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {companiesTotalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {((companiesPage - 1) * companiesPerPage) + 1} to {Math.min(companiesPage * companiesPerPage, companiesTotal)} of {companiesTotal} results
                  </div>
                  <div className="pagination-controls">
                    <select
                      value={companiesPerPage}
                      onChange={(e) => {
                        setCompaniesPerPage(Number(e.target.value));
                        setCompaniesPage(1);
                      }}
                      className="pagination-select"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <button
                      onClick={() => setCompaniesPage(prev => Math.max(1, prev - 1))}
                      disabled={companiesPage === 1}
                      className="pagination-button"
                    >
                      Previous
                    </button>
                    <span className="pagination-page-info">
                      Page {companiesPage} of {companiesTotalPages || 1}
                    </span>
                    <button
                      onClick={() => setCompaniesPage(prev => Math.min(companiesTotalPages, prev + 1))}
                      disabled={companiesPage >= companiesTotalPages}
                      className="pagination-button"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">No employers found</div>
          )}
        </div>
      )}

      {/* Subcontractors Section */}
      {activeMenu === 'subcontractors' && (
        <div className="subcontractors-section">
          <div className="section-header">
            <h2>All Subcontractors</h2>
          </div>

          {subcontractorsLoading ? (
            <div className="loading-state">Loading subcontractors...</div>
          ) : subcontractors.length > 0 ? (
            <>
              <div className="table-container">
                <table className="subcontractors-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Trade</th>
                      <th>Day Rate</th>
                      <th>CIS Rate</th>
                      <th>VAT Number</th>
                      <th>UTR</th>
                      <th>Employers</th>
                      <th>Registration Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subcontractors.map((sub) => (
                      <tr
                        key={sub.user_id}
                        onClick={() => {
                          setSelectedUserDetail(sub);
                          setUserDetailType('subcontractor');
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{sub.full_name || 'N/A'}</td>
                        <td>{sub.email}</td>
                        <td>{sub.phone_number || 'N/A'}</td>
                        <td>{sub.trade || 'N/A'}</td>
                        <td>{sub.day_rate ? `£${parseFloat(sub.day_rate).toFixed(2)}` : 'N/A'}</td>
                        <td>{sub.cis_rate ? `${sub.cis_rate}%` : 'N/A'}</td>
                        <td>{sub.vat_number || 'N/A'}</td>
                        <td>{sub.unique_tax_reference || 'N/A'}</td>
                        <td>
                          {sub.employers && sub.employers.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {sub.employers.map((employer, index) => (
                                <span key={index} style={{ 
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}>
                                  {employer}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>No employers</span>
                          )}
                        </td>
                        <td>{sub.created_at ? new Date(sub.created_at).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          <span className={`status-badge status-${sub.status}`}>
                            {sub.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {subcontractorsTotalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {((subcontractorsPage - 1) * subcontractorsPerPage) + 1} to {Math.min(subcontractorsPage * subcontractorsPerPage, subcontractorsTotal)} of {subcontractorsTotal} results
                  </div>
                  <div className="pagination-controls">
                    <select
                      value={subcontractorsPerPage}
                      onChange={(e) => {
                        setSubcontractorsPerPage(Number(e.target.value));
                        setSubcontractorsPage(1);
                      }}
                      className="pagination-select"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <button
                      onClick={() => setSubcontractorsPage(prev => Math.max(1, prev - 1))}
                      disabled={subcontractorsPage === 1}
                      className="pagination-button"
                    >
                      Previous
                    </button>
                    <span className="pagination-page-info">
                      Page {subcontractorsPage} of {subcontractorsTotalPages || 1}
                    </span>
                    <button
                      onClick={() => setSubcontractorsPage(prev => Math.min(subcontractorsTotalPages, prev + 1))}
                      disabled={subcontractorsPage >= subcontractorsTotalPages}
                      className="pagination-button"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">No subcontractors found</div>
          )}
        </div>
      )}

      {/* User Detail Modal */}
      {selectedUserDetail && userDetailType && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => { setSelectedUserDetail(null); setUserDetailType(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>{userDetailType === 'employer' ? 'Employer Details' : 'Subcontractor Details'}</h2>
              <button className="modal-close" onClick={() => { setSelectedUserDetail(null); setUserDetailType(null); }}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              {userDetailType === 'employer' ? (
                <div className="user-detail-grid" style={{ display: 'grid', gap: '16px' }}>
                  <DetailRow label="Organisation name" value={employerOrganizationDisplayName(selectedUserDetail)} />
                  <DetailRow label="Contact name" value={selectedUserDetail.full_name || 'N/A'} />
                  <DetailRow label="Email" value={selectedUserDetail.email} />
                  <DetailRow label="Phone" value={selectedUserDetail.phone_number || 'N/A'} />
                  <DetailRow
                    label="Company Type"
                    value={
                      String(selectedUserDetail.company_type || '').trim().toUpperCase() === 'LTD'
                        ? 'Ltd company'
                        : 'Sole trader'
                    }
                  />
                  {String(selectedUserDetail.company_type || '').trim().toUpperCase() === 'LTD' && (
                    <DetailRow
                      label="Registered Name"
                      value={selectedUserDetail.organization_name || selectedUserDetail.registered_name || 'N/A'}
                    />
                  )}
                  <DetailRow label="VAT Number" value={selectedUserDetail.vat_number || 'N/A'} />
                  <DetailRow label="UTR" value={selectedUserDetail.unique_tax_reference || 'N/A'} />
                  <DetailRow label="Address" value={[selectedUserDetail.address, selectedUserDetail.city, selectedUserDetail.postal_code].filter(Boolean).join(', ') || 'N/A'} />
                  <DetailRow label="Registration Status" value={selectedUserDetail.company_registration_status || 'N/A'} />
                  <DetailRow label="Account Status" value={selectedUserDetail.status || 'N/A'} />
                  <DetailRow label="HMRC Connected" value={selectedUserDetail.hmrc_connected ? 'Yes' : 'No'} />
                  <DetailRow label="Created" value={selectedUserDetail.created_at ? new Date(selectedUserDetail.created_at).toLocaleString() : 'N/A'} />
                  <DetailRow label="Last Login" value={selectedUserDetail.last_login_at ? new Date(selectedUserDetail.last_login_at).toLocaleString() : 'N/A'} />
                </div>
              ) : (
                <div className="user-detail-grid" style={{ display: 'grid', gap: '16px' }}>
                  <DetailRow label="Name" value={selectedUserDetail.full_name || 'N/A'} />
                  <DetailRow label="Email" value={selectedUserDetail.email} />
                  <DetailRow label="Phone" value={selectedUserDetail.phone_number || 'N/A'} />
                  <DetailRow label="Trade" value={selectedUserDetail.trade || 'N/A'} />
                  <DetailRow label="Day Rate" value={selectedUserDetail.day_rate ? `£${parseFloat(selectedUserDetail.day_rate).toFixed(2)}` : 'N/A'} />
                  <DetailRow label="CIS Rate" value={selectedUserDetail.cis_rate ? `${selectedUserDetail.cis_rate}%` : 'N/A'} />
                  <DetailRow label="VAT Number" value={selectedUserDetail.vat_number || 'N/A'} />
                  <DetailRow label="UTR" value={selectedUserDetail.unique_tax_reference || 'N/A'} />
                  <DetailRow label="NI Number" value={selectedUserDetail.ni_number || 'N/A'} />
                  <DetailRow label="Company Name" value={selectedUserDetail.company_name_sub || 'N/A'} />
                  <DetailRow label="Address" value={[selectedUserDetail.address, selectedUserDetail.city, selectedUserDetail.postal_code].filter(Boolean).join(', ') || 'N/A'} />
                  <DetailRow label="Email Verified" value={selectedUserDetail.email_verified ? 'Yes' : 'No'} />
                  <DetailRow label="Status" value={selectedUserDetail.status || 'N/A'} />
                  <DetailRow label="Employers" value={selectedUserDetail.employers && selectedUserDetail.employers.length > 0 ? selectedUserDetail.employers.join(', ') : 'None'} />
                  <DetailRow label="Created" value={selectedUserDetail.created_at ? new Date(selectedUserDetail.created_at).toLocaleString() : 'N/A'} />
                  <DetailRow label="Last Login" value={selectedUserDetail.last_login_at ? new Date(selectedUserDetail.last_login_at).toLocaleString() : 'N/A'} />
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedCompany && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={closeRejectModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Reject Employer Registration</h2>
              <button className="modal-close" onClick={closeRejectModal}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to reject the registration for <strong>{employerOrganizationDisplayName(selectedCompany)}</strong>?</p>
              <div className="form-field-group">
                <label className="form-label">Rejection Reason (Optional)</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  rows={4}
                  className="form-input"
                />
              </div>
              <div className="modal-actions">
                <button onClick={closeRejectModal} className="cancel-button" disabled={actionLoading}>
                  Cancel
                </button>
                <button onClick={handleRejectCompany} className="btn-reject" disabled={actionLoading}>
                  {actionLoading ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Payments Section */}
      {activeMenu === 'payments' && (
        <div className="payments-section">
          <div className="section-header">
            <h2>All Payments</h2>
          </div>

          {/* Filters */}
          <div className="filters-container" style={{ marginBottom: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <label style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Month:</label>
              <select
                value={paymentsMonth || ''}
                onChange={(e) => {
                  setPaymentsMonth(e.target.value ? Number(e.target.value) : null);
                  setPaymentsPage(1);
                }}
                className="filter-select"
              >
                <option value="">All Months</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                  <option key={month} value={month}>
                    {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <label style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Year:</label>
              <select
                value={paymentsYear}
                onChange={(e) => {
                  setPaymentsYear(Number(e.target.value));
                  setPaymentsPage(1);
                }}
                className="filter-select"
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <label style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Employer:</label>
              <select
                value={paymentsEmployerFilter}
                onChange={(e) => {
                  setPaymentsEmployerFilter(e.target.value);
                  setPaymentsPage(1);
                }}
                className="filter-select"
                style={{ minWidth: '200px' }}
              >
                <option value="">All Employers</option>
                {allEmployers.map(employer => (
                  <option key={employer.user_id} value={employer.user_id}>
                    {employerOrganizationDisplayName(employer)}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <label style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Status:</label>
              <select
                value={paymentsStatusFilter}
                onChange={(e) => {
                  setPaymentsStatusFilter(e.target.value);
                  setPaymentsPage(1);
                }}
                className="filter-select"
                style={{ minWidth: '150px' }}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="paid">Paid</option>
                <option value="withdrawn">Withdrawn</option>
              </select>
            </div>
          </div>

          {/* Summary Section */}
          {paymentsSummary && (
            <div className="payments-summary" style={{ 
              marginBottom: '20px', 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '15px' 
            }}>
              {Object.entries(paymentsSummary.by_status).map(([status, stats]) => (
                stats.count > 0 && (
                  <div key={status} className="summary-card" style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '8px',
                    padding: '15px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{ 
                      fontSize: '14px', 
                      color: 'rgba(255, 255, 255, 0.8)', 
                      textTransform: 'capitalize',
                      marginBottom: '8px'
                    }}>
                      {status}
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: '700', 
                      color: '#fff',
                      marginBottom: '4px'
                    }}>
                      {stats.count}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: 'rgba(255, 255, 255, 0.7)',
                      marginBottom: '4px'
                    }}>
                      Total: £{stats.total_amount.toFixed(2)}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: 'rgba(255, 255, 255, 0.7)'
                    }}>
                      Payable: £{stats.total_payable.toFixed(2)}
                    </div>
                  </div>
                )
              ))}
              <div className="summary-card" style={{
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                padding: '15px',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  Grand Total
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700', 
                  color: '#fff',
                  marginBottom: '4px'
                }}>
                  {paymentsTotal}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginBottom: '4px'
                }}>
                  Total Amount: £{paymentsSummary.grand_total_amount.toFixed(2)}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: 'rgba(255, 255, 255, 0.7)'
                }}>
                  Total Payable: £{paymentsSummary.grand_total_payable.toFixed(2)}
                </div>
              </div>
            </div>
          )}

          {paymentsLoading ? (
            <div className="loading-state">Loading payments...</div>
          ) : payments.length > 0 ? (
            <>
              <div className="table-container">
                <table className="companies-table">
                  <thead>
                    <tr>
                      <th>Payment ID</th>
                      <th>Employer</th>
                      <th>Subcontractor</th>
                      <th>Type</th>
                      <th>Net Payable</th>
                      <th>Status</th>
                      <th>Submitted Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.payment_request_id}>
                        <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {payment.payment_request_id.substring(0, 8)}...
                        </td>
                        <td>
                          <div>
                            <div>{payment.employer_name || 'N/A'}</div>
                            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                              {payment.employer_email}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div>{payment.subcontractor_name || 'N/A'}</div>
                            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                              {payment.subcontractor_email}
                            </div>
                          </div>
                        </td>
                        <td>{payment.payment_type === 'job_rate' ? 'Job Rate' : 'By Day'}</td>
                        <td>{formatPaymentNetPayable(payment)}</td>
                        <td>
                          <span className={`status-badge status-${payment.status}`}>
                            {payment.status}
                          </span>
                        </td>
                        <td>{payment.submitted_at ? new Date(payment.submitted_at).toLocaleDateString() : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {paymentsTotalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {((paymentsPage - 1) * paymentsPerPage) + 1} to {Math.min(paymentsPage * paymentsPerPage, paymentsTotal)} of {paymentsTotal} results
                  </div>
                  <div className="pagination-controls">
                    <select
                      value={paymentsPerPage}
                      onChange={(e) => {
                        setPaymentsPerPage(Number(e.target.value));
                        setPaymentsPage(1);
                      }}
                      className="pagination-select"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <button
                      onClick={() => setPaymentsPage(prev => Math.max(1, prev - 1))}
                      disabled={paymentsPage === 1}
                      className="pagination-button"
                    >
                      Previous
                    </button>
                    <span className="pagination-page-info">
                      Page {paymentsPage} of {paymentsTotalPages || 1}
                    </span>
                    <button
                      onClick={() => setPaymentsPage(prev => Math.min(paymentsTotalPages, prev + 1))}
                      disabled={paymentsPage >= paymentsTotalPages}
                      className="pagination-button"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">No payments found</div>
          )}
        </div>
      )}

      {/* Monthly Returns Section */}
      {activeMenu === 'monthly-returns' && (
        <div className="companies-section">
          <div className="section-header">
            <h2>Monthly Returns (CIS)</h2>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>CIS Tax Month</label>
              <select
                value={monthlyReturnsYear && monthlyReturnsMonth ? `${monthlyReturnsYear}-${monthlyReturnsMonth}` : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) {
                    const [y, m] = v.split('-').map(Number);
                    setMonthlyReturnsYear(y);
                    setMonthlyReturnsMonth(m);
                  } else {
                    setMonthlyReturnsYear(null);
                    setMonthlyReturnsMonth(null);
                  }
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.3)',
                  background: '#fff',
                  color: '#000',
                  minWidth: '200px'
                }}
              >
                <option value="">Select month</option>
                {availableCISMonths.map((m) => (
                  <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Employer filter</label>
              <select
                value={monthlyReturnsEmployerFilter}
                onChange={(e) => {
                  setMonthlyReturnsEmployerFilter(e.target.value);
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.3)',
                  background: '#fff',
                  color: '#000',
                  minWidth: '220px'
                }}
              >
                <option value="">All employers</option>
                {allEmployers.map((emp) => (
                  <option key={emp.user_id} value={emp.user_id}>
                    {employerOrganizationDisplayName(emp)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {monthlyReturnsLoading ? (
            <div className="loading-state">Loading monthly returns...</div>
          ) : monthlyReturnsList.length > 0 ? (
            <div className="table-container">
              <table className="companies-table">
                <thead>
                  <tr>
                    <th>Employer</th>
                    <th>CIS Month</th>
                    <th>Payments</th>
                    <th>Total Gross (£)</th>
                    <th>CIS Deducted (£)</th>
                    <th>Total Net (£)</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyReturnsList.map((row) => (
                    <tr key={row.employer_id}>
                      <td>{row.employer_name || 'N/A'}</td>
                      <td>{row.cis_month_label}</td>
                      <td>{row.payment_count}</td>
                      <td>{row.total_gross != null ? `£${Number(row.total_gross).toFixed(2)}` : '—'}</td>
                      <td>{row.total_cis_deducted != null ? `£${Number(row.total_cis_deducted).toFixed(2)}` : '—'}</td>
                      <td>{row.total_net_paid != null ? `£${Number(row.total_net_paid).toFixed(2)}` : '—'}</td>
                      <td>
                        <span className={`status-badge ${row.submitted ? 'status-approved' : 'status-pending'}`}>
                          {row.submitted ? (row.submitted_at ? new Date(row.submitted_at).toLocaleDateString() : 'Yes') : 'No'}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => openMonthlyReturnsDetail(row.employer_id)}
                          className="view-button"
                          style={{
                            padding: '6px 12px',
                            background: 'rgba(91, 192, 222, 0.3)',
                            border: '1px solid #57bedd',
                            color: '#57bedd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          View detail
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              {monthlyReturnsYear && monthlyReturnsMonth
                ? 'No monthly return data for the selected month and filters.'
                : 'Select a CIS tax month to view monthly returns.'}
            </div>
          )}

          {/* Detail modal */}
          {showMonthlyReturnsDetailModal && (
            <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowMonthlyReturnsDetailModal(false)}>
              <div className="modal-content" style={{ background: '#1a1f2e', borderRadius: '12px', maxWidth: '700px', width: '90%', maxHeight: '85vh', overflow: 'auto', padding: '24px' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, color: '#fff' }}>Monthly return detail</h3>
                  <button type="button" onClick={() => setShowMonthlyReturnsDetailModal(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer' }}>×</button>
                </div>
                {monthlyReturnsDetailLoading ? (
                  <div className="loading-state">Loading...</div>
                ) : monthlyReturnsDetail ? (
                  <>
                    <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '16px' }}>
                      {monthlyReturnsDetail.company_name} — {monthlyReturnsDetail.cis_month_label} ({monthlyReturnsDetail.cis_month_range})
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px', fontSize: '14px' }}>
                      <div><strong>Total gross:</strong> £{Number(monthlyReturnsDetail.total_gross).toFixed(2)}</div>
                      <div><strong>Total CIS deducted:</strong> {monthlyReturnsDetail.total_cis_deducted != null ? `£${Number(monthlyReturnsDetail.total_cis_deducted).toFixed(2)}` : '—'}</div>
                      <div><strong>Total net paid:</strong> £{Number(monthlyReturnsDetail.total_net_paid).toFixed(2)}</div>
                    </div>
                    <h4 style={{ color: '#fff', marginBottom: '10px' }}>By subcontractor</h4>
                    <div className="table-container">
                      <table className="companies-table">
                        <thead>
                          <tr>
                            <th>Subcontractor</th>
                            <th>Labour</th>
                            <th>CIS deducted</th>
                            <th>Net paid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(monthlyReturnsDetail.subcontractor_summaries || []).map((s) => (
                            <tr key={s.subcontractor_id}>
                              <td>{s.subcontractor_name}</td>
                              <td>£{Number(s.total_labor).toFixed(2)}</td>
                              <td>{s.total_cis_deducted != null ? `£${Number(s.total_cis_deducted).toFixed(2)}` : '—'}</td>
                              <td>£{Number(s.total_net_paid).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p style={{ color: 'rgba(255,255,255,0.7)' }}>No detail available.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cron Logs Section */}
      {activeMenu === 'cron-logs' && (
        <CronLogsTab />
      )}

      {/* Action Logs Section */}
      {activeMenu === 'action-logs' && (
        <ActionLogsTab />
      )}

      {/* SMTP Settings Section */}
      {activeMenu === 'smtp' && (
        <div className="overview-section">
          <h2 style={{ marginBottom: '20px', fontSize: '24px' }}>SMTP Configuration</h2>
          
          {smtpMessage.text && (
            <div className={`message ${smtpMessage.type}`} style={{ marginBottom: '20px', padding: '15px' }}>
              {smtpMessage.text}
            </div>
          )}

          {smtpLoading ? (
            <div className="loading-state">Loading SMTP settings...</div>
          ) : (
            <form onSubmit={handleUpdateSMTPSettings} style={{ maxWidth: '800px' }}>
              <div style={{ display: 'grid', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    SMTP Host
                  </label>
                  <input
                    type="text"
                    value={smtpSettings.smtp_host}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    SMTP Port
                  </label>
                  <input
                    type="number"
                    value={smtpSettings.smtp_port}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: e.target.value })}
                    placeholder="587"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    SMTP Username
                  </label>
                  <input
                    type="text"
                    value={smtpSettings.smtp_user}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_user: e.target.value })}
                    placeholder="your-email@domain.com"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    SMTP Password
                  </label>
                  <input
                    type="text"
                    value={smtpSettings.smtp_password}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_password: e.target.value })}
                    placeholder="Leave blank to keep current password"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                  <small style={{ display: 'block', marginTop: '5px', opacity: 0.8 }}>
                    Leave blank to keep the current password unchanged
                  </small>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={smtpSettings.smtp_tls}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_tls: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Enable TLS</span>
                  </label>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    From Email Address
                  </label>
                  <input
                    type="email"
                    value={smtpSettings.emails_from_email}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, emails_from_email: e.target.value })}
                    placeholder="noreply@yourdomain.com"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    From Name
                  </label>
                  <input
                    type="text"
                    value={smtpSettings.emails_from_name}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, emails_from_name: e.target.value })}
                    placeholder="PayCIS"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Brevo API Key (optional)
                  </label>
                  <input
                    type="text"
                    value={smtpSettings.brevo_api_key}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, brevo_api_key: e.target.value })}
                    placeholder="xkeysib-... Leave blank to use SMTP or keep current key"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                  <small style={{ display: 'block', marginTop: '5px', opacity: 0.8 }}>
                    When set, emails are sent via Brevo Transactional API instead of SMTP. Use your Brevo API key (v3). Leave blank to keep current key or use SMTP only.
                  </small>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={smtpSaving}
                    style={{
                      padding: '12px 24px',
                      background: smtpSaving ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                      border: '2px solid rgba(255, 255, 255, 0.4)',
                      color: '#fff',
                      borderRadius: '8px',
                      cursor: smtpSaving ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: '500',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      if (!smtpSaving) {
                        e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!smtpSaving) {
                        e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                      }
                    }}
                  >
                    {smtpSaving ? 'Saving...' : 'Save SMTP Settings'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Test Email Section */}
          <div style={{ 
            marginTop: '40px', 
            padding: '20px', 
            border: '1px solid rgba(255, 255, 255, 0.3)', 
            borderRadius: '8px',
            background: 'rgba(255, 255, 255, 0.05)'
          }}>
            <h3 style={{ marginBottom: '15px', fontSize: '20px' }}>Test SMTP Configuration</h3>
            <p style={{ marginBottom: '20px', opacity: 0.9, fontSize: '14px' }}>
              Send a test email to verify that your SMTP settings are working correctly.
            </p>
            <div style={{ 
              marginBottom: '20px', 
              padding: '12px', 
              background: 'rgba(255, 193, 7, 0.15)', 
              border: '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '6px',
              fontSize: '13px'
            }}>
              <strong style={{ color: '#ffc107' }}>⚠️ Important:</strong> For Brevo/Sendinblue, ensure your "From Email Address" domain is verified in your Brevo account. 
              Unverified sender domains will cause emails to be accepted but not delivered (and credits won't be used).
            </div>
            <form onSubmit={handleTestEmail} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Test Email Address
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  disabled={testEmailSending}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={testEmailSending || !testEmail}
                style={{
                  padding: '12px 24px',
                  background: (testEmailSending || !testEmail) ? 'rgba(255, 255, 255, 0.3)' : 'rgba(77, 182, 172, 0.8)',
                  border: '2px solid rgba(77, 182, 172, 0.6)',
                  color: '#fff',
                  borderRadius: '8px',
                  cursor: (testEmailSending || !testEmail) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'all 0.3s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  if (!testEmailSending && testEmail) {
                    e.target.style.background = 'rgba(77, 182, 172, 1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!testEmailSending && testEmail) {
                    e.target.style.background = 'rgba(77, 182, 172, 0.8)';
                  }
                }}
              >
                {testEmailSending ? 'Sending...' : 'Send Test Email'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;

